import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { BrandingRecord } from '../api/types'
import { DEFAULT_BRANDING, fetchBranding, loadCachedBranding, onBrandingFetched } from './branding'

/*
  The platform branding record (brief §2.6): product name, short name, descriptor,
  credit line, support contact. Every display string that names the product comes from
  here through `useBranding()`; the build check (no-literals.test.ts) fails on a literal
  anywhere else.

  Resolution order: the API (`GET /api/v1/platform/branding`, anonymous), else the copy
  cached in IndexedDB `meta` from the last online start, else `branding.default.json`,
  the same file that seeds the database and generates the manifest (A-MANIFEST).
*/

export interface BrandingState {
  branding: BrandingRecord
  /** Where the record came from, for the About page and support. */
  source: 'default' | 'cache' | 'api'
}

const BrandingContext = createContext<BrandingState>({
  branding: DEFAULT_BRANDING,
  source: 'default',
})

export function BrandingProvider({
  children,
  initial,
}: {
  children: ReactNode
  initial?: BrandingRecord
}) {
  const [state, setState] = useState<BrandingState>(() => ({
    branding: initial ?? DEFAULT_BRANDING,
    source: initial ? 'cache' : 'default',
  }))

  // A refresh from the sync cycle (the origin-migration flag, an edited credit line).
  useEffect(() => onBrandingFetched((record) => setState({ branding: record, source: 'api' })), [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (!initial) {
        const cached = await loadCachedBranding()
        if (cached && !cancelled) setState({ branding: cached, source: 'cache' })
      }
      if (!navigator.onLine) return
      try {
        const fresh = await fetchBranding()
        if (!cancelled) setState({ branding: fresh, source: 'api' })
      } catch {
        // Offline or the API is down: the cached or default record stands.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [initial])

  const value = useMemo(() => state, [state])
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
}

/** The platform branding record. Always complete. */
// eslint-disable-next-line react-refresh/only-export-components
export function useBranding(): BrandingRecord {
  return useContext(BrandingContext).branding
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBrandingSource(): BrandingState['source'] {
  return useContext(BrandingContext).source
}
